import { request } from "../helpers/axios.new";
import { useMutation, useQuery } from "@tanstack/react-query";
import { announceBonusConfigSaved } from "./bonusConfigStore";

export const FETCH_GROUPS = (companyId) => {
  return request({
    url: `/group/${companyId}`,
    method: "GET",
  });
};

export const FETCH_GROUP = (groupId) =>
  useQuery(["fetchGroup"], () => request({ url: `/group/${groupId}` }));

export const FETCH_GROUP_TYPE = (typeId) =>
  useQuery(["fetchGroupType", typeId], () => request({ url: `/group/type/${typeId}` }), {staleTime: Infinity });

export const FETCH_GROUP_TYPES = () =>
  useQuery(["fetchGroupTypes"], () => request({ url: `/group/types` }), {staleTime: Infinity });

export const ADD_GROUP = () =>
  useMutation(({ companyId, data }) =>
    request({
      method: "POST",
      url: `/group/${companyId}`,
      data,
    }),
  );

export const ADD_SUB_GROUP = () =>
  useMutation(({ companyId, data }) =>
    request({
      method: "POST",
      url: `/group/sub/${companyId}`,
      data,
    }),
  );

export const UPDATE_GROUP = () =>
  useMutation(({ groupId, data }) =>
    request({
      method: "PUT",
      url: `/group/${groupId}`,
      data,
    }),
  );

export const UPDATE_SUB_GROUP = () =>
  useMutation(({ groupId, data }) =>
    request({
      method: "PUT",
      url: `/group/sub/${groupId}`,
      data,
    }),
  );

// Per-workspace sales bonus setup (Team Room → "Milestones & bonuses" and the
// dashboard editor's Setup tab). SERVER ONLY — the config lives in the
// database; there is no localStorage mirror and no demo fallback.
//
// SAVE_BONUS_CONFIG is a plain async function so non-component code (the
// dashboard's Publish handler) can call it directly — invoking the useMutation
// factory from an event handler is an invalid hook call and throws.
export const SAVE_BONUS_CONFIG = async ({ groupId, data }) => {
  const res = await request({
    method: "PUT",
    url: `/group/${groupId}/bonus-config`,
    data,
  });
  announceBonusConfigSaved(groupId); // mounted dashboards refetch immediately
  return res?.data ?? res;
};

export const UPDATE_BONUS_CONFIG = () => useMutation(SAVE_BONUS_CONFIG);

// Per-rep bonus progress (YTD ladder + this-month commission) for a workspace.
// The server is the single source of truth: no demo snapshot, no local
// overlay. An unconfigured workspace renders the cards' own empty/setup
// states instead of stand-in data.
// `window` narrows the progress window: period string or { from, to }.
// `opts.program` = Analytics card override (POST) — does not touch workspace.
//
// Setup is where this endpoint hurts: editing a rule re-renders the preview and
// the card re-requests the base window PLUS every Applies-to range × workspace.
// Each call is a heavy server-side revenue roll-up, so a naive setup session
// fires the same range dozens of times. A short-lived result cache + in-flight
// coalescing collapses those into one request each; `opts.fresh` (or a save)
// bypasses it so a real data change is never masked.
const bonusProgressCache = new Map(); // key → { at, data }
const bonusProgressInflight = new Map(); // key → Promise
const BONUS_PROGRESS_TTL = 60 * 1000;

const bonusProgressKey = (groupId, range, program) =>
  `${groupId}|${range.period || ""}|${range.from || ""}|${range.to || ""}|${
    program ? JSON.stringify(program) : ""
  }`;

export const clearBonusProgressCache = () => {
  bonusProgressCache.clear();
  bonusProgressInflight.clear();
};

export const FETCH_BONUS_PROGRESS = async (groupId, window = null, opts = {}) => {
  const program = opts && opts.program ? opts.program : null;
  const range = {};
  if (typeof window === "string") {
    if (window && window !== "month") range.period = window;
  } else if (window && (window.from || window.to)) {
    if (window.from) range.from = window.from;
    if (window.to) range.to = window.to;
  }

  const cacheKey = bonusProgressKey(groupId, range, program);
  if (opts.fresh) {
    bonusProgressCache.delete(cacheKey);
    bonusProgressInflight.delete(cacheKey);
  } else {
    const cached = bonusProgressCache.get(cacheKey);
    if (cached && Date.now() - cached.at < BONUS_PROGRESS_TTL) return cached.data;
    const pending = bonusProgressInflight.get(cacheKey);
    if (pending) return pending;
  }

  const run = (async () => {
    let res;
    if (program) {
      res = await request({
        url: `/group/${groupId}/bonus-progress`,
        method: "POST",
        data: { program, ...range },
      });
    } else {
      let qs = "";
      if (range.period) qs = `?period=${range.period}`;
      else if (range.from || range.to) {
        const q = [];
        if (range.from) q.push(`from=${range.from}`);
        if (range.to) q.push(`to=${range.to}`);
        if (q.length) qs = `?${q.join("&")}`;
      }
      res = await request({
        url: `/group/${groupId}/bonus-progress${qs}`,
        method: "GET",
      });
    }
    return res?.data ?? res;
  })()
    .then((data) => {
      bonusProgressCache.set(cacheKey, { at: Date.now(), data });
      return data;
    })
    .finally(() => {
      bonusProgressInflight.delete(cacheKey);
    });

  bonusProgressInflight.set(cacheKey, run);
  return run;
};

export const ADD_GROUP_MEMBER = () =>
  useMutation(({ groupId, data }) =>
    request({
      method: "POST",
      url: `/group/${groupId}/member`,
      data,
    }),
  );

export const REMOVE_GROUP_MEMBER = () =>
  useMutation(({ userId }) =>
    request({
      method: "DELETE",
      url: `/group/member/${userId}`,
    }),
  );

export const DELETE_GROUP = () =>
  useMutation(({ groupId }) =>
    request({
      method: "DELETE",
      url: `/group/${groupId}`,
    }),
  );


export const PUT_WORKSPACE_IMAGE = (imageData) =>
  request({
    method: "PUT",
    url: `/group/image-upload`,
    data: imageData,
  });

export const SUBMIT_AWARD_PRICE = (body) => {
    return request({
      url: '/group/submit-award-price',
      method: "PATCH",
      data: body,
    });
  };
  
export const GET_AWARD = (groupId) => {
  return request({
    url: `/group/get-award/${groupId}`,
  });
};

export const GET_BACKGROUND = (groupId) => {
  return request({
    url: `/group/get-background/${groupId}`,
  });
};

export const SUBMIT_BGIMAGE = (body) => {
  return request({
    url: '/group/submit-background',
    method: "PATCH",
    data: body,
  });
};

// Set or clear (empty string) the workspace's external playbook link.
export const UPDATE_PLAYBOOK_URL = (groupId, playbookUrl) => {
  return request({
    url: `/group/${groupId}/playbook-url`,
    method: "PATCH",
    data: { playbookUrl },
  });
};

export const SUBMIT_BADGE = (body) => {
  return request({
    url: '/group/submit-badge',
    method: "PATCH",
    data: body,
  });
};

export const SUBMIT_PROOF = (body) => {
  return request({
    url: '/group/submit-proof',
    method: "PATCH",
    data: body,
  });
};

export const DELETE_BADGE = (badgeId) => {
  return request({
    url: `/group/delete-badge`,
    method: "PATCH",
    data: { badgeId }
  });
};

export const CREATE_USER_BADGE = (body) => {
  return request({
    url: '/group/create-user-badges',
    method: "PATCH",
    data: body,
  });
};